import { programUrl } from '@client/new/helpers/landingPages';
import { Influencer, Program } from '@playbook-technologies/common-enums';
import {
  LiteProgram,
  Price,
  ProgramTabHash,
  RawCollection,
  WorkoutTiny,
} from './domain';
import { filterNulls } from '@client/new/helpers/general';
import { SalesPageConfig } from './collection.model';
import { isEmpty, pick } from 'lodash';
import { COLLECTION_SETTINGS } from '@client/components/collections/collections.settings';
import { validateCollection } from '@client/components/collections/validation';
import { isTreeHealthy } from './helpers';
import { Collection } from './collection.domain';
import { SubscriptionLPLink } from './publishModal/SubscriptionLPLink';
import { toProgramLandingPageUrl } from '@client/helpers/routes';
import { ProgramPageProps } from './collection.viewModel';
import { F1 } from '@client/new/helpers/types';
import { AccessType } from '../collections/collections.domain';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import {
  BundleStatus,
  BundlePricing,
  GetCollectionPageDataQuery,
} from 'generated/graphql';

export const toWorkoutTiny = (ws: RawCollection['workouts']) =>
  Array.isArray(ws)
    ? filterNulls(ws).map(
        ({ id, name = '' }) =>
          ({ workoutId: id, name } as unknown as WorkoutTiny),
      )
    : [];

export const getType = (accessType: string): LiteProgram['programType'] =>
  accessType === Program.AccessType.Exclusive.value ? 'otp' : 'subscription';

const getTags = <
  T extends Pick<
    RawCollection,
    'isVisible' | 'isLocked' | 'archivedAt' //| 'deletedAt'
  >,
>(
  collection: T,
): LiteProgram['tags'] => {
  const tags: LiteProgram['tags'] = [
    collection?.isVisible
      ? { text: 'live', type: 'ok' }
      : { text: 'not published', type: 'warn' },
  ];

  if (collection?.isLocked) tags.push({ text: 'locked', type: 'info' });
  //   if (collection?.deletedAt) tags.push({ text: 'removed', type: 'warn' });
  if (collection?.archivedAt) tags.push({ text: 'archived', type: 'warn' });
  return tags;
};

const toSubtitle = (duration?: Array<string | null>, workouts?: number) => {
  const durationtext =
    duration && duration.length >= 2 ? `${duration[0]} ${duration[1]}` : '';
  const workoutsText =
    `${workouts} ${workouts === 1 ? 'workout' : 'workouts'}` || '';

  return `${durationtext} ${
    durationtext && workoutsText && ', '
  } ${workoutsText}`;
};

export const formatPrice = (value: number | null) =>
  value !== null
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value / 100)
    : '';

export const toThumbnailUrl = (
  v:
    | { cdn01?: string | null | undefined; image?: string | null | undefined }
    | null
    | undefined,
) => (v?.cdn01 || '') + (v?.image || '');

export type LiteProgramType = 'Program' | 'Category' | '';

export const toCategoryType = (type: number): LiteProgramType => {
  switch (type) {
    case Program.Type.Collection.value:
      return 'Category';
    case Program.Type.Program.value:
      return 'Program';
    default:
      trackSessionEvent('Error', {
        value: `Missing program type during mapping @toCategoryType. Received type: ${
          type ? type : typeof type
        }`,
      });
      return '';
  }
};

export const toLiteProgram = ({
  collection,
  price,
  customURL,
  salesPageConfig,
}: {
  collection: RawCollection;
  price?: Price;
  customURL: string;
  salesPageConfig?: SalesPageConfig;
}): LiteProgram => ({
  programId: collection.id,
  programType: getType(collection.accessType),
  programState: collection.isVisible ? 'published' : 'not-publish-ready',
  tags: getTags(collection),
  categoryType: toCategoryType(collection.type),

  title: collection.name || '',
  subtitle: toSubtitle(
    collection.duration || undefined,
    collection?.workouts?.length,
  ),

  thumbnailUrl: toThumbnailUrl(collection.mediaCover),
  hasSalesPageConfig: !isEmpty(salesPageConfig),
  workouts: toWorkoutTiny(collection.workouts || []),
  salesPageUrl: programUrl(
    { customURL },
    { id: collection.id, name: collection.name || '' },
  ),
  price: price?.price ? formatPrice(price?.price) : '',
  discountedPrice: !price?.discountedPrice
    ? null
    : formatPrice(price.discountedPrice),
  accessType: collection.accessType as AccessType,
  migrationBundleId: collection.migrationBundleId ?? null,
});

export const getSuccessModalData = ({
  accessType,
  customURL,
  profileDeepLink,
  programDeepLink,
  liteProgram,
  creatorId,
  isCreatorLive,
  hasExclusivePricing,
  dismiss,
  goHome,
}: {
  accessType: string;
  customURL?: string;
  profileDeepLink?: string;
  programDeepLink?: string;
  liteProgram: LiteProgram;
  creatorId: number;
  isCreatorLive: boolean;
  hasExclusivePricing: boolean;
  dismiss: () => void;
  goHome: () => void;
}) => {
  if (accessType == Program.AccessType.Exclusive.value) {
    const landingPageLink = toProgramLandingPageUrl({
      id: liteProgram.programId,
      name: liteProgram.title,
      influencer: { customURL },
    });
    if (!isCreatorLive) {
      return {
        title: "It's Published!",
        subtitle: "You've just published your One-Time Purchase Program!",
        programData: pick(liteProgram, [
          'title',
          'subtitle',
          'thumbnailUrl',
          'price',
          'discountedPrice',
        ]),
        bottomText:
          'To become live on the platform return to Home tab and click Go Live button',
        cta: [
          {
            label: 'Dismiss',
            onClick: dismiss,
            variant: 'text',
            className: 'w-[150px]',
          },
          {
            label: 'Go to Home',
            onClick: goHome,
            className: 'w-[150px]',
          },
        ],
      };
    }

    return {
      title: 'It’s Published!',
      subtitle:
        'Published programs are automatically visible to your audience.',
      links: [
        {
          title: 'Program Landing Page',
          url: landingPageLink,
          desc: `Use this link to direct users to your Program's Landing page, with the full context of your program`,
        },
        ...(hasExclusivePricing
          ? [
              {
                title: 'Program Checkout Page',
                url: `${landingPageLink}/checkout`,
                desc: `Links a user straight to where they can check out and purchase your program`,
              },
            ]
          : []),
        {
          title: 'Program Deep Link',
          url: programDeepLink || '',
          desc: `Use this link to direct user straight to your Program's content.`,
        },
      ],
      programData: pick(liteProgram, [
        'title',
        'subtitle',
        'thumbnailUrl',
        'price',
        'discountedPrice',
      ]),
      cta: [
        {
          label: 'Dismiss',
          onClick: dismiss,
          className: 'w-[150px]',
        },
      ],
    };
  }

  if (!isCreatorLive) {
    return {
      title: 'It’s Published!',
      subtitle:
        'You’ve just published a collection of workouts to your subscription business!',
      programData: pick(liteProgram, ['title', 'subtitle', 'thumbnailUrl']),
      bottomText:
        'To become live on the platform return to Home tab and click Go Live button',
      cta: [
        {
          label: 'Dismiss',
          onClick: dismiss,
          variant: 'text',
          className: 'w-[150px]',
        },
        {
          label: 'Go to Home',
          onClick: goHome,
          className: 'w-[150px]',
        },
      ],
      bottomComponent: SubscriptionLPLink({
        creatorId,
        programId: liteProgram.programId,
      }),
    };
  }

  const landingPageLink = customURL
    ? `${import.meta.env.VITE_LANDING_PAGE_URL}/${customURL}`
    : '';

  return {
    title: 'It’s Published!',
    subtitle: 'Start promoting your program & share links!.',
    showDismiss: true,
    links: [
      {
        title: 'Program Landing Page',
        url: landingPageLink,
        desc: `Use this link to direct users to your Program's Landing page, with the full context of your program`,
      },
      {
        title: 'Program Deep Link',
        url: profileDeepLink ?? '',
        desc: `Use this link to direct user straight to your Program's content.`,
      },
    ],
  };
};

export const getPublishInfo = (
  hasPublishedAnything: boolean,
  accessType: string,
) => {
  if (hasPublishedAnything) return;
  if (accessType == Program.AccessType.Exclusive.value)
    return 'As soon as you publish your first paid program, your channel will be discoverable on the Playbook app! Promote it to expand your reach and grow your audience. If you need more time, save your program as a draft.';
  return 'As soon as you publish your first collection, your channel will be discoverable on the Playbook app! Promote it to expand your reach and grow your audience. If you need more time, save your program as a draft.';
};

export const getMappedCollection = (
  collection: RawCollection & { targetArea?: string[] },
): Collection => {
  return {
    ...collection,
    workouts: collection.workouts || [],
    targetArea: (collection.targetArea || []).filter(
      (x) => x && COLLECTION_SETTINGS.COLLECTION_TARGET_AREA.includes(x),
    ) as string[],
    mediaIntro: collection.mediaIntro || undefined,
    mediaCover: collection.mediaCover || undefined,
    hiddenSections: collection.hiddenSections || {
      faq: false,
      testimonials: true,
    },
  } as any;
};

type ProgramPropsMapperFn = F1<LiteProgram, ProgramPageProps['tabs']>;

const defaultWorkoutTab = 'workouts-published';

export const toSubscriptionTabs: ProgramPropsMapperFn = (program) => ({
  [defaultWorkoutTab]: { isReady: program.workouts.length > 0 },
  about: { isReady: true },
  'sales-page': { isReady: program.hasSalesPageConfig },
  ...(program.categoryType === 'Program'
    ? { challenges: { isReady: false } }
    : {}),
});

export const toNewFeatureOtpTabs = (
  program: LiteProgram,
  isPartOfAnyPackage: boolean,
): ProgramPageProps['tabs'] => ({
  [defaultWorkoutTab]: { isReady: program.workouts.length > 0 },
  about: { isReady: true },
  'pricing-packages': { isReady: isPartOfAnyPackage },
  challenges: { isReady: false },
});

export const isAboutTabReady = (collection: Collection): boolean => {
  try {
    return isTreeHealthy(validateCollection(collection, false));
  } catch {
    return false;
  }
};

export const getTabWithWorkoutDefault = (tab: ProgramTabHash): ProgramTabHash =>
  tab.includes('workouts-') ? defaultWorkoutTab : tab;

export const toPublishBundleIds = (
  publishToPlatformBundle: boolean,
  bundles?: Array<{
    id: number;
    isPlatformBundle: boolean;
    status: BundleStatus;
    pricing: BundlePricing;
  }>,
): Array<number> | null => {
  if (!bundles) {
    return null;
  }

  const subscriptionBundles = bundles.filter(
    (b) =>
      b.pricing === BundlePricing.Subscription &&
      b.status === BundleStatus.Published,
  );

  const isPlatformBundleCreator =
    !isEmpty(subscriptionBundles) &&
    subscriptionBundles.every(
      (b) => b.isPlatformBundle && b.status === BundleStatus.Published,
    );

  if (isPlatformBundleCreator) {
    const platformBundle = bundles.find(
      (b) =>
        b.status === BundleStatus.Published &&
        b.pricing === BundlePricing.Subscription &&
        b.isPlatformBundle,
    );

    const exclusiveBundle = bundles.find(
      (b) =>
        b.status === BundleStatus.Draft &&
        b.pricing === BundlePricing.Subscription &&
        !b.isPlatformBundle,
    );

    const bundleIds = platformBundle?.id ? [platformBundle?.id] : [];

    if (exclusiveBundle?.id) {
      bundleIds.push(exclusiveBundle?.id);
    }

    return bundleIds;
  }

  const platformBundle = bundles.find(
    (b) =>
      b.status === BundleStatus.Archived &&
      b.pricing === BundlePricing.Subscription &&
      b.isPlatformBundle,
  );
  const exclusiveBundle = bundles.find(
    (b) =>
      b.status === BundleStatus.Published &&
      b.pricing === BundlePricing.Subscription &&
      !b.isPlatformBundle,
  );
  const bundleIds = exclusiveBundle?.id ? [exclusiveBundle?.id] : [];
  if (publishToPlatformBundle && platformBundle?.id) {
    bundleIds.push(platformBundle?.id);
  }
  return bundleIds;
};
